import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import numeral from 'numeral';
import Axios from 'app/axios';

import MillieApi     from 'app/apis/millie';
import EllipsisMenu  from 'app/components/common/ellipsis-menu';
import Icon          from 'app/components/common/icon';
import Modal         from 'app/components/common/modal';
import StandardInput from 'app/components/common/standard-input';
import cdn           from 'app/helpers/cdn';
import helpers       from 'app/helpers/files';


const allCommonTypes = Object.values(helpers.CommonTypes);

class FileInput extends React.PureComponent {

  constructor(props) {
    super(props);

    this.state = {
      qFile: null,
      name: '',
      ext: null,
      uploading: false,
      uploadFailed: false,
      uploadValidations: null,
    };

    this.refInput = React.createRef();

    this.onClickAdd = this.onClickAdd.bind(this);
    this.onCloseModal = this.onCloseModal.bind(this);
    this.onChangeName = this.onChangeName.bind(this);
    this.onChangeFiles = this.onChangeFiles.bind(this);
    this.onClickUpload = this.onClickUpload.bind(this);
    this.onClickRemoveQf = this.onClickRemoveQf.bind(this);
    this.onUpload = this.onUpload.bind(this);
  }

  get isMaxed() {
    return this.props.files.length >= this.props.maxFiles;
  }

  get typeObjs() {
    const types = this.props.types?.length ? this.props.types : allCommonTypes;
    return types.map(type => helpers.typeObjs[type]).filter(o => o);
  }
  get accepts() {
    const accepts = [];
    this.typeObjs.forEach((type) => {
      accepts.push(...type.accepts);
    });
    return accepts;
  }
  get acceptLabels() {
    return this.typeObjs.map(type => type.label);
  }

  get readOnly() {
    return !this.props.onChange;
  }

  onChangeFiles(event) {
    const qFile = event.target.files[0] || null;
    let [name, ext] = helpers.getNameAndExt(qFile?.name);
    name = helpers.sanitizeFilename(name);
    ext = (ext || '').toLowerCase();
    this.setState({qFile, name, ext});
  }

  onCloseModal() {
    this.setState({qFile: null, name: '', ext: null});
  }
  onChangeName(event, inputStr) {
    const name = inputStr.replace(/\s/g, '_').replace(/[^0-9a-zA-Z-_]/g, '-');
    this.setState({name});
  }

  onClickAdd() {
    this.refInput.current.click();
  }
  onClickUpload() {
    this.upload();
  }
  onClickRemoveQf() {
    this.setState({qFile: null});
  }

  onClickDelete(index, event) {
    event.preventDefault();
    const files = [...this.props.files];
    files.splice(index, 1);
    this.props.onChange(files);
  }

  onUpload(filePath) {
    const files = [...this.props.files, filePath];
    this.props.onChange(files);
  }

  async upload() {
    const {qFile, name, ext} = this.state;
    const fileName = `${name}${ext.toLowerCase()}`;
    this.setState({uploading: true, uploadFailed: false, uploadValidations: null});
    let pathValue, didFail;
    try {
      const {key, signedUrl} = await MillieApi.uploadCreatePresignedUrlV2(fileName, qFile.type);
      pathValue = key;
      await Axios.put(signedUrl, qFile, {headers: {'Content-Type': qFile.type}});
    } catch (error) {
      const uploadValidations = error.response?.data?.error?.validations || null;
      didFail = true;
      this.setState({uploading: false, uploadFailed: true, uploadValidations});
    }
    if (didFail) return;
    this.setState({uploading: false, qFile: null, name: '', ext: null});
    this.onUpload(pathValue);
  }
 
  renderQFile() {
    const {qFile, name, ext, uploading, uploadFailed, uploadValidations} = this.state;
    const {maxSize} = this.props;
    if (!qFile) return null;
    const {size, type} = qFile;
    const isTooBig = size > maxSize;
    const hasExt = !!ext;
    const hasType = !!qFile.type;
    const uploadLabel = uploading ? 'Uploading...' : 'Upload';
    const vmsg = (() => {
      if (!hasType) return 'Unrecognized file type';
      if (!hasExt) return 'Unrecognized extension';
      if (isTooBig) return `Size exceeds limit of ${numeral(maxSize).format('0b')}`;
      if (uploadValidations?.fileType?.length) return uploadValidations.fileType[0];
      return null;
    })();
    const uploadDisabled = (() => {
      if (vmsg) return true;
      if (uploading) return true;
      if (this.isMaxed) return true;
      if (!name.replace(/[^0-9a-zA-Z]+/g, '')) return true;
      return false;
    })();
    let subtext = numeral(size).format('0.0b');
    const typeObj = helpers.getTypeFromExt(ext, qFile.type);
    if (typeObj) subtext += ` ${typeObj.label}`;

    return (
      <Modal className="bform" onClose={this.onCloseModal}>
        <h1 className="bform-h1">Upload File</h1>

        <label className="bform-h3">File</label>
        <div className={`_border _border-all _py-6 _px-12 _bg-ice _br-2 ${vmsg ? '_border _border-all _py-6 _px-12 _border-danger _br-2' : ''}`}>
          <div className="_fw-bold">{qFile.name}</div>
          <div className="_clr-slate">{subtext}</div>
        </div>
        {vmsg && <div className="validation-message">{vmsg}</div>}

        {!vmsg && (<>
          <label className="bform-h3">Name</label>
          <StandardInput value={name} onChange={this.onChangeName} name="fileName" label="My_Document" maxChars={60} validations={uploadValidations} />
        </>)}

        <div className="bform-actions">
          <div className="bform-actions-right">
            <button className="btn secondary" onClick={this.onClickRemoveQf}>Cancel</button>
            {!!vmsg ? (<>
              <button className="btn blue" onClick={this.onClickAdd}>Select Different File</button>
            </>) : (<>
              <button className="btn blue" onClick={this.onClickUpload} disabled={uploadDisabled}>{uploadLabel}</button>
            </>)}
          </div>
        </div>

        {!!uploadFailed && (
          <div className="bform-error">Oops! Something went wrong.</div>
        )}
      </Modal>
    );
  }

  render() {
    const {qFile} = this.state;
    const {className, files, maxFiles, onGray, ghostBtn} = this.props;
    const showAddMore = (() => {
      if (this.readOnly) return false;
      if (qFile) return false;
      if (this.isMaxed) return false;
      return true;
    })();
    const hasFiles = !!files.length;

    return (
      <div className={`file-input ${className}`}>
        <input type="file" ref={this.refInput} onChange={this.onChangeFiles} multiple className="file-input-actual" accept={this.accepts.join(', ')} />
        {this.renderQFile()}

        {files.map((file, i) => {
          const url = cdn.assetUrl(file);
          const name = file.split('/').pop();
          return (
            <div key={i} className={`file-input-file ${onGray ? 'on-gray' : ''}`}>
              <Icon.Attachment1 className="file-input-file-icon" />
              <a className="file-input-file-name pink-hover" href={url} target="_blank">{name}</a>
              <EllipsisMenu usePortal className="alt1">
                <a href={url} target="_blank" >View File</a>
                {!this.readOnly && (
                  <a href="#" onClick={this.onClickDelete.bind(this, i)}>Delete</a>
                )}
              </EllipsisMenu>
            </div>
          );
        })}

        {showAddMore && (
          <div className={`_flex _justify-start _align-center _gap-12 ${hasFiles ? '_mt-12' : ''}`}>
            <button onClick={this.onClickAdd} className={`btn small blue ${(hasFiles || ghostBtn) ? 'secondary ghost' : ''}`}>Add a File</button>
            <div className="_clr-slate _italic _fs-12">{`Accepts: ${this.acceptLabels.join(', ')}`}</div>
          </div>
        )}
      </div>
    );
  }

}

FileInput.propTypes = {
  files: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func, // when null, this component will be read-only
  maxFiles: PropTypes.number,
  maxSize: PropTypes.number,
  className: PropTypes.string,
  types: PropTypes.arrayOf(PropTypes.oneOf(allCommonTypes)),
  onGray: PropTypes.bool,
  ghostBtn: PropTypes.bool,
};

FileInput.defaultProps = {
  maxFiles: 1,
  maxSize: 25_000_000,
  className: '',
  types: allCommonTypes,
  onGray: false,
  ghostBtn: false,
};

export default FileInput;
